import React, { useMemo } from 'react';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import useTranslations from 'hooks/useTranslations';
import UserPicker from 'components/_pickers/UserPicker';
import { useEisRuleConfigContext } from '../context';

import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import type { EisNotificationRecipient } from '../../_types';

const NotificationsSection: React.FC = () => {
  const t = useTranslations();
  const {
    organizationId,
    notifySupervisors,
    setNotifySupervisors,
    notifyAdditional,
    setNotifyAdditional,
    additionalRecipients,
    setAdditionalRecipients,
  } = useEisRuleConfigContext();

  const handleNotifyAdditionalChange = (checked: boolean) => {
    setNotifyAdditional(checked);
    if (!checked) {
      setAdditionalRecipients([]);
    }
  };

  const handleRecipientsChange = (users: User[]) => {
    const recipients: EisNotificationRecipient[] = users.map((user) => ({
      id: user.id!,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
    }));
    setAdditionalRecipients(recipients);
  };

  const recipientsAsUsers: User[] = useMemo(
    () =>
      additionalRecipients.map((r) => {
        const [firstName = '', ...rest] = r.name.split(' ');
        return {
          id: r.id,
          firstName,
          lastName: rest.join(' '),
          email: r.email,
        } as User;
      }),
    [additionalRecipients],
  );

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Checkbox
            checked={notifySupervisors}
            onChange={(e) => setNotifySupervisors(e.target.checked)}
          />
        }
        label={
          <Stack>
            <Typography variant='bodyMd'>
              {t('performanceReporting.eis.configuration.setNotifications.notifications.supervisors.label')}
            </Typography>
            <Typography
              variant='bodySm'
              color='text.secondary'
            >
              {t('performanceReporting.eis.configuration.setNotifications.notifications.supervisors.helperText')}
            </Typography>
          </Stack>
        }
        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={notifyAdditional}
            onChange={(e) => handleNotifyAdditionalChange(e.target.checked)}
          />
        }
        label={
          <Stack>
            <Typography variant='bodyMd'>
              {t('performanceReporting.eis.configuration.setNotifications.notifications.additional.label')}
            </Typography>
            <Typography
              variant='bodySm'
              color='text.secondary'
            >
              {t('performanceReporting.eis.configuration.setNotifications.notifications.additional.helperText')}
            </Typography>
          </Stack>
        }
        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
      />

      {notifyAdditional && (
        <UserPicker
          id='eis-additional-recipients'
          organizationId={organizationId}
          value={recipientsAsUsers}
          onChange={handleRecipientsChange}
          allowMultiple
          placeholder={t('performanceReporting.eis.configuration.setNotifications.notifications.recipientsPlaceholder')}
          size='small'
          sx={{ maxWidth: 480 }}
        />
      )}
    </Stack>
  );
};

export default NotificationsSection;
